import React from "react";

type CommandProps<T extends HTMLElement> = React.HTMLAttributes<T> & {
  value?: string;
  shouldFilter?: boolean;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  [key: string]: unknown;
};

function createPrimitive<T extends HTMLElement>(tag: keyof JSX.IntrinsicElements, displayName: string) {
  const Component = React.forwardRef<T, CommandProps<T>>(({ children, onValueChange: _onValueChange, shouldFilter: _shouldFilter, ...props }, ref) =>
    React.createElement(tag, { ...props, ref }, children as React.ReactNode)
  );
  Component.displayName = displayName;
  return Component;
}

const Root = createPrimitive<HTMLDivElement>("div", "Command");
type CommandInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  onValueChange?: (value: string) => void;
};

const Input = React.forwardRef<HTMLInputElement, CommandInputProps>(({ onValueChange, onChange, ...props }, ref) => (
  <input
    ref={ref}
    onChange={(event) => {
      onChange?.(event);
      onValueChange?.(event.currentTarget.value);
    }}
    {...props}
  />
));
Input.displayName = "CommandInput";

type CommandItemProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect"> & {
  disabled?: boolean;
  onSelect?: (value: string) => void;
  value?: string;
};

const Item = React.forwardRef<HTMLDivElement, CommandItemProps>(
  ({ onClick, onKeyDown, onSelect, value, disabled, tabIndex, role, ...props }, ref) => {
    const selectValue = String(value ?? "");
    const triggerSelect = () => {
      if (disabled) return;
      onSelect?.(selectValue);
    };

    return (
      <div
        ref={ref}
        role={role ?? "option"}
        tabIndex={tabIndex ?? (disabled ? undefined : 0)}
        aria-disabled={disabled === true ? true : undefined}
        data-value={selectValue}
        data-disabled={disabled === true ? true : undefined}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          triggerSelect();
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (event.defaultPrevented) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          triggerSelect();
        }}
        {...props}
      />
    );
  }
);
Item.displayName = "CommandItem";

export const Command = Object.assign(Root, {
  Input,
  List: createPrimitive<HTMLDivElement>("div", "CommandList"),
  Empty: createPrimitive<HTMLDivElement>("div", "CommandEmpty"),
  Group: createPrimitive<HTMLDivElement>("div", "CommandGroup"),
  Item,
  Separator: createPrimitive<HTMLDivElement>("div", "CommandSeparator"),
});
